import React, { createContext, FC, PropsWithChildren, useContext } from 'react';

import { ISingleCluster } from '../../../../entities/IClusters';
import { ClusterPermissions } from '../../../../entities/IPermissions';

interface IClusterContextProps {
    currentClusterId: string;
    isTeamsRoute: boolean;
    currentCluster: ISingleCluster;
    setClusterId: (id: string) => void;
    setCurrentCluster: (cluster: ISingleCluster | undefined) => void;
    currentClusterPermissions: { [key in ClusterPermissions]?: boolean };
    setClusterPermissions: (permissions: string[]) => void;
    clearCurrentCluster: () => void;
    setIsTeamsRoute: (isTeams: boolean) => void;
    /** Gdy true, PanelContainer nie otwiera panelu z URL – czeka na załadowanie klastra */
    deferPanelFromUrl: boolean;
    setDeferPanelFromUrl: (v: boolean) => void;
    pendingPanelSearch: string | null;
    setPendingPanelSearch: (v: string | null) => void;
}

const ClusterContext = createContext<IClusterContextProps | undefined>(undefined);

export const useClusterContext = () => {
    const context = useContext(ClusterContext);
    if (!context) {
        throw new Error('useClusterContext must be used within a ClusterProvider');
    }
    return context;
};

/** Same context as useClusterContext, but returns undefined outside ClusterProvider (safe for shared components). */
export const useOptionalClusterContext = () => useContext(ClusterContext);

export const ClusterContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const [clusterId, setClusterId] = React.useState<string>('');
    const [isTeamsRoute, setIsTeamsRoute] = React.useState<boolean>(false);
    const [currentCluster, setCurrentCluster] = React.useState<ISingleCluster | undefined>(undefined);
    const [currentClusterPermissions, setCurrentClusterPermissions] = React.useState<{ [key in ClusterPermissions]?: boolean }>(undefined);
    const [deferPanelFromUrl, setDeferPanelFromUrl] = React.useState<boolean>(false);
    const [pendingPanelSearch, setPendingPanelSearch] = React.useState<string | null>(null);

    const clearCurrentCluster = () => {
        setClusterId('');
        setCurrentCluster(undefined);
        setCurrentClusterPermissions(undefined);
    };

    const setClusterPermissions = (permissions: string[] | Array<{ name?: string }> | undefined) => {
        const list = Array.isArray(permissions)
            ? permissions.map((p) => (typeof p === 'string' ? p : p?.name ?? '')).filter(Boolean)
            : [];
        setCurrentClusterPermissions(
            list.reduce<{ [key: string]: boolean }>((acc, key) => ({ ...acc, [key]: true }), {})
        );
    };

    const initState: IClusterContextProps = {
        currentClusterId: clusterId,
        setClusterId,
        currentCluster,
        setCurrentCluster,
        currentClusterPermissions,
        setClusterPermissions,
        clearCurrentCluster,
        isTeamsRoute,
        setIsTeamsRoute,
        deferPanelFromUrl,
        setDeferPanelFromUrl,
        pendingPanelSearch,
        setPendingPanelSearch
    };

    return (
        <ClusterContext.Provider
            value={initState}
        >
            {children}
        </ClusterContext.Provider>
    );
};